import type { MlbTiebreakerTeam, TiebreakerResult, GameResultsLookup, TiebreakerOptions, StepContext } from '../../shared/tiebreaker/types';
import { RankMethod } from '../../shared/enums/rank-method';
import { createContext } from '../../shared/tiebreaker/step-runner';
import {
  groupByDescending, bestOf, computeHthRecords, hthWinPct,
  groupByHthPct, findSweepWinner, filterDivisionWinners,
  addExplanation, addExplanationAll,
} from '../../shared/tiebreaker/helpers';

export interface MlbTiebreakerOptions extends TiebreakerOptions {
  skipDivisionWinners?: boolean;
}

/**
 * MLB Two-way tiebreaker: resolves exactly 2 teams.
 * HTH → divisionWinner → div% → conf% → coinflip
 */
export function mlbTwoWayTiebreaker(
  teams: MlbTiebreakerTeam[],
  games: GameResultsLookup,
  options?: MlbTiebreakerOptions,
): TiebreakerResult<MlbTiebreakerTeam> {
  const ctx = createContext<MlbTiebreakerTeam>(games, options);
  const [a, b] = teams;

  // Step 1: H2H
  const hthRecords = computeHthRecords(teams, ctx.games);
  const aPct = hthWinPct(hthRecords, a.teamAbbreviation);
  const bPct = hthWinPct(hthRecords, b.teamAbbreviation);

  if (aPct > bPct && aPct > 0) {
    addExplanation(ctx, a, `HTH: ${(aPct * 100).toFixed(1)}%.`);
    addExplanation(ctx, b, `HTH: ${(bPct * 100).toFixed(1)}%.`);
    ctx.rankMethods.set(a.teamAbbreviation, RankMethod.HTHTwoWay);
    ctx.rankMethods.set(b.teamAbbreviation, RankMethod.HTHTwoWay);
    return result(ctx, [a, b]);
  }
  if (bPct > aPct && bPct > 0) {
    addExplanation(ctx, b, `HTH: ${(bPct * 100).toFixed(1)}%.`);
    addExplanation(ctx, a, `HTH: ${(aPct * 100).toFixed(1)}%.`);
    ctx.rankMethods.set(a.teamAbbreviation, RankMethod.HTHTwoWay);
    ctx.rankMethods.set(b.teamAbbreviation, RankMethod.HTHTwoWay);
    return result(ctx, [b, a]);
  }

  // Step 2: Division winner
  if (a.isDivisionWinner && !b.isDivisionWinner) {
    ctx.rankMethods.set(a.teamAbbreviation, RankMethod.DivisionWinner);
    ctx.rankMethods.set(b.teamAbbreviation, RankMethod.NonDivisionWinner);
    return result(ctx, [a, b]);
  }
  if (b.isDivisionWinner && !a.isDivisionWinner) {
    ctx.rankMethods.set(b.teamAbbreviation, RankMethod.DivisionWinner);
    ctx.rankMethods.set(a.teamAbbreviation, RankMethod.NonDivisionWinner);
    return result(ctx, [b, a]);
  }

  // Step 3: Div%
  if (a.records.division.pctNaN > b.records.division.pctNaN && a.records.division.pctNaN >= 0) {
    ctx.rankMethods.set(a.teamAbbreviation, RankMethod.TwoWayDivPCT);
    ctx.rankMethods.set(b.teamAbbreviation, RankMethod.TwoWayDivPCT);
    return result(ctx, [a, b]);
  }
  if (b.records.division.pctNaN > a.records.division.pctNaN && b.records.division.pctNaN >= 0) {
    ctx.rankMethods.set(a.teamAbbreviation, RankMethod.TwoWayDivPCT);
    ctx.rankMethods.set(b.teamAbbreviation, RankMethod.TwoWayDivPCT);
    return result(ctx, [b, a]);
  }

  // Step 4: Conf%
  if (a.records.conference.pctNaN > b.records.conference.pctNaN && a.records.conference.pctNaN >= 0) {
    ctx.rankMethods.set(a.teamAbbreviation, RankMethod.TwoWayConfPCT);
    ctx.rankMethods.set(b.teamAbbreviation, RankMethod.TwoWayConfPCT);
    return result(ctx, [a, b]);
  }
  if (b.records.conference.pctNaN > a.records.conference.pctNaN && b.records.conference.pctNaN >= 0) {
    ctx.rankMethods.set(a.teamAbbreviation, RankMethod.TwoWayConfPCT);
    ctx.rankMethods.set(b.teamAbbreviation, RankMethod.TwoWayConfPCT);
    return result(ctx, [b, a]);
  }

  // Step 5: Coin flip
  const winner = ctx.coinFlipFn(teams);
  const loser = teams.find(t => t !== winner)!;
  ctx.rankMethods.set(winner.teamAbbreviation, RankMethod.CoinFlip);
  ctx.rankMethods.set(loser.teamAbbreviation, RankMethod.CoinFlip);
  addExplanation(ctx, winner, 'Coin flip.');
  return result(ctx, [winner, loser]);
}

/**
 * MLB Multi-way tiebreaker: resolves 3+ teams.
 * Division-winner separation → sweep → HTH → div% → conf% → coinflip
 * Partial breaks restart from HTH (bug fix).
 */
export function mlbMultiWayTiebreaker(
  teams: MlbTiebreakerTeam[],
  games: GameResultsLookup,
  skipDivWinner: boolean,
  options?: MlbTiebreakerOptions,
): TiebreakerResult<MlbTiebreakerTeam> {
  const ctx = createContext<MlbTiebreakerTeam>(games, options);
  const ordered: MlbTiebreakerTeam[] = [];
  let remaining = [...teams];
  const maxIterations = teams.length * 10;
  let iterations = 0;

  while (remaining.length > 1) {
    if (iterations++ > maxIterations) {
      // Safety: coin flip everything
      while (remaining.length > 0) {
        const w = ctx.coinFlipFn(remaining);
        ctx.rankMethods.set(w.teamAbbreviation, RankMethod.CoinFlip);
        ordered.push(w);
        remaining = remaining.filter(t => t !== w);
      }
      break;
    }

    // Delegate to two-way if only 2 remain
    if (remaining.length === 2) {
      const twoWayResult = mlbTwoWayTiebreaker(remaining, games, options);
      ordered.push(...twoWayResult.orderedTeams);
      // Merge rank methods and explanations
      for (const [k, v] of twoWayResult.rankMethods) ctx.rankMethods.set(k, v);
      for (const [k, v] of twoWayResult.explanations) {
        const existing = ctx.explanations.get(k) ?? [];
        ctx.explanations.set(k, [...existing, ...v]);
      }
      remaining = [];
      break;
    }

    // Step 1: Division winner separation (if not skipped)
    if (!skipDivWinner) {
      const { divisionWinners, nonWinners } = filterDivisionWinners(remaining);
      if (divisionWinners.length > 0 && nonWinners.length > 0) {
        // Separate: division winners first, then non-winners
        for (const w of divisionWinners) {
          ctx.rankMethods.set(w.teamAbbreviation, RankMethod.DivisionWinnerMultiWay);
          ordered.push(w);
        }
        remaining = nonWinners;
        continue;
      }
    }

    // Step 2: Sweep check
    const hthRecords = computeHthRecords(remaining, ctx.games);
    const sweepWinner = findSweepWinner(remaining, hthRecords) as MlbTiebreakerTeam | null;
    if (sweepWinner) {
      ctx.rankMethods.set(sweepWinner.teamAbbreviation, RankMethod.HTHFullBreakMultiWay);
      addExplanation(ctx, sweepWinner, 'H2H sweep winner.');
      ordered.push(sweepWinner);
      remaining = remaining.filter(t => t !== sweepWinner);
      continue; // Restart from step 1 for remainder
    }

    // Step 3: HTH grouping
    const hthGroups = groupByHthPct(remaining, ctx.games);
    if (hthGroups.length > 1) {
      // Partial break: take top group, restart remainder from here (HTH)
      const topGroup = hthGroups[0].teams as MlbTiebreakerTeam[];
      const rest = hthGroups.slice(1).flatMap(g => g.teams) as MlbTiebreakerTeam[];

      if (topGroup.length === 1) {
        ctx.rankMethods.set(topGroup[0].teamAbbreviation, RankMethod.HTHPartialBreakMultiWay);
        ordered.push(topGroup[0]);
        remaining = rest;
        continue; // Restart from HTH (bug fix: not from prevTiebreaker)
      }
      // Top group has multiple teams — need further tiebreaking within it
      // Place them then handle rest
      if (topGroup.length === 2) {
        const sub = mlbTwoWayTiebreaker(topGroup, games, options);
        ordered.push(...sub.orderedTeams);
        for (const [k, v] of sub.rankMethods) ctx.rankMethods.set(k, v);
        for (const [k, v] of sub.explanations) {
          const existing = ctx.explanations.get(k) ?? [];
          ctx.explanations.set(k, [...existing, ...v]);
        }
      } else {
        const sub = mlbMultiWayTiebreaker(topGroup, games, true, options);
        ordered.push(...sub.orderedTeams);
        for (const [k, v] of sub.rankMethods) ctx.rankMethods.set(k, v);
        for (const [k, v] of sub.explanations) {
          const existing = ctx.explanations.get(k) ?? [];
          ctx.explanations.set(k, [...existing, ...v]);
        }
      }
      remaining = rest;
      continue;
    }

    // Step 4: Div%
    const bestDiv = bestOf(remaining, t => t.records.division.pctNaN);
    if (bestDiv.length < remaining.length && bestDiv.length > 0 && bestDiv[0].records.division.pctNaN >= 0) {
      if (bestDiv.length === 1) {
        ctx.rankMethods.set(bestDiv[0].teamAbbreviation, RankMethod.DivPctFullBreakMultiWay);
        ordered.push(bestDiv[0]);
        remaining = remaining.filter(t => t !== bestDiv[0]);
        continue;
      }
      // Partial break by div%
      for (const t of bestDiv) {
        ctx.rankMethods.set(t.teamAbbreviation, RankMethod.DivPctPartialBreakMultiWay);
      }
      // Resolve the best group, then handle remainder
      if (bestDiv.length === 2) {
        const sub = mlbTwoWayTiebreaker(bestDiv, games, options);
        ordered.push(...sub.orderedTeams);
        for (const [k, v] of sub.rankMethods) ctx.rankMethods.set(k, v);
      } else {
        const sub = mlbMultiWayTiebreaker(bestDiv, games, true, options);
        ordered.push(...sub.orderedTeams);
        for (const [k, v] of sub.rankMethods) ctx.rankMethods.set(k, v);
      }
      remaining = remaining.filter(t => !bestDiv.includes(t));
      continue;
    }

    // Step 5: Conf%
    const bestConf = bestOf(remaining, t => t.records.conference.pctNaN);
    if (bestConf.length < remaining.length && bestConf.length > 0 && bestConf[0].records.conference.pctNaN >= 0) {
      if (bestConf.length === 1) {
        ctx.rankMethods.set(bestConf[0].teamAbbreviation, RankMethod.ConfPctFullBreakMultiWay);
        ordered.push(bestConf[0]);
        remaining = remaining.filter(t => t !== bestConf[0]);
        continue;
      }
      for (const t of bestConf) {
        ctx.rankMethods.set(t.teamAbbreviation, RankMethod.ConfPctFullBreakMultiWay);
      }
      if (bestConf.length === 2) {
        const sub = mlbTwoWayTiebreaker(bestConf, games, options);
        ordered.push(...sub.orderedTeams);
        for (const [k, v] of sub.rankMethods) ctx.rankMethods.set(k, v);
      } else {
        const sub = mlbMultiWayTiebreaker(bestConf, games, true, options);
        ordered.push(...sub.orderedTeams);
        for (const [k, v] of sub.rankMethods) ctx.rankMethods.set(k, v);
      }
      remaining = remaining.filter(t => !bestConf.includes(t));
      continue;
    }

    // Step 6: Coin flip one at a time
    const w = ctx.coinFlipFn(remaining);
    ctx.rankMethods.set(w.teamAbbreviation, RankMethod.CoinFlip);
    addExplanation(ctx, w, 'Multi-way exhausted. Coin flip.');
    ordered.push(w);
    remaining = remaining.filter(t => t !== w);
  }

  if (remaining.length === 1) {
    ordered.push(remaining[0]);
  }

  return { orderedTeams: ordered, rankMethods: ctx.rankMethods, explanations: ctx.explanations };
}

/**
 * MLB Conference ranker: groups teams by win%, dispatches tied groups.
 */
export function mlbConferenceRanker(
  teams: MlbTiebreakerTeam[],
  games: GameResultsLookup,
  options?: MlbTiebreakerOptions,
): TiebreakerResult<MlbTiebreakerTeam> {
  const ctx = createContext<MlbTiebreakerTeam>(games, options);
  const ordered: MlbTiebreakerTeam[] = [];

  const pctGroups = groupByDescending(teams, t => t.records.all.pctNaN);
  for (const group of pctGroups) {
    if (group.length === 1) {
      ctx.rankMethods.set(group[0].teamAbbreviation, RankMethod.AllPCT);
      ordered.push(group[0]);
    } else if (group.length === 2) {
      const sub = mlbTwoWayTiebreaker(group, games, options);
      ordered.push(...sub.orderedTeams);
      for (const [k, v] of sub.rankMethods) ctx.rankMethods.set(k, v);
      for (const [k, v] of sub.explanations) {
        const existing = ctx.explanations.get(k) ?? [];
        ctx.explanations.set(k, [...existing, ...v]);
      }
    } else {
      const sub = mlbMultiWayTiebreaker(group, games, false, options);
      ordered.push(...sub.orderedTeams);
      for (const [k, v] of sub.rankMethods) ctx.rankMethods.set(k, v);
      for (const [k, v] of sub.explanations) {
        const existing = ctx.explanations.get(k) ?? [];
        ctx.explanations.set(k, [...existing, ...v]);
      }
    }
  }

  return { orderedTeams: ordered, rankMethods: ctx.rankMethods, explanations: ctx.explanations };
}

/**
 * MLB Division pick top: pick the best team in a division.
 */
export function mlbDivisionPickTop(
  teams: MlbTiebreakerTeam[],
  games: GameResultsLookup,
  options?: MlbTiebreakerOptions,
): MlbTiebreakerTeam {
  if (teams.length === 1) return teams[0];
  const ranked = mlbConferenceRanker(teams, games, options);
  return ranked.orderedTeams[0];
}

// ─── Internal helpers ───────────────────────────────────────────────────────

function result<T extends MlbTiebreakerTeam>(
  ctx: StepContext<T>,
  orderedTeams: T[],
): TiebreakerResult<T> {
  return { orderedTeams, rankMethods: ctx.rankMethods, explanations: ctx.explanations };
}
